"use strict"

import lodash from "lodash"

import BootstrapService from "../bootstrap/bootstrap.service"
import AssignmentService from "../assignment/assignment.service"
import AssignmentWorkService from "../assignment-work/assignment-work.service"
import RosterService from "../roster/roster.service"
import ClassCodeService from "../class-code/class-code.service"
import UserService from "../user/user.service"
import AuthService, { UserTokenInfo } from "../auth/auth.service"
import MessageService from "../message/message.service"
import NotificationService from "../notification/notification.service"
import HelpRequestService from "../help-request/help-request.service"
import StickerService from "../sticker/sticker.service"
import ContractService from "../contract/contract.service"
import StudentCacheService from "../student-cache/student-cache.service"
import SubscriptionService from "../subscription/subscription.service"
import ABTestService, {
  ABTest,
  ABTestVariable,
} from "../ab-test/ab-test-service"
import StorageService from "../storage/storage.service"
import FeedbackService from "../feedback/feedback.service"
import FirebaseService from "../firebase/firebase.service"
import OrganizationService from "../organization/organization.service"
import HelpArticleService from "../help-article/help-article.service"
import AnalyticsService from "../analytics/analytics.service"
import SharedWorksService from "../shared-works/shared-works.service"
import CoTeacherService from "../co-teacher/co-teacher.service"
import LazyVar from "../../model/util/lazy-var"
import MessageSet from "../../model/domain/message-set"
import SessionData from "../../model/domain/session-data"
import Helper from "../../model/domain/helper"
import ProInfo from "../../model/domain/pro-info"
import { AppConfiguration } from "../../model/domain/app-configuration"
import Organization, {
  OrganizationTypes,
} from "../../model/domain/organization"
import SharedWorkSessionData from "../../model/domain/shared-work-session-data"
import Assignment from "../../model/domain/assignment"
import AssignmentRoster from "../../model/domain/assignment-roster"
import AssignmentWork from "../../model/domain/assignment-work"
import Roster from "../../model/domain/roster"
import ClassCode from "../../model/domain/class-code"
import User from "../../model/domain/user"
import UserSticker from "../../model/domain/user-sticker"
import Contract from "../../model/domain/contract"
import CoTeacher from "../../model/domain/co-teacher"
import ChildOrganizationCount from "../../model/domain/child-organizations-count"
import ActiveHelper from "../../model/domain/active-helper"
import UserStatusEditor from "../../model/domain/user-status-editor"
import Subscription from "../../model/domain/subscription"
import HelpArticle from "../../model/domain/help-article"
import { SharedWorkUser } from "../../model/domain/shared-work"

// eslint-disable-next-line import/no-default-export
export default class CacheService {
  private assignment = new LazyVar<Promise<Assignment>>()
  private assignmentWork = new LazyVar<Promise<AssignmentWork>>()
  private assignmentWorks = new LazyVar<Promise<Map<string, AssignmentWork>>>()
  private assignments = new LazyVar<Promise<Map<string, Assignment>>>()
  private studentAssignmentsAndWorks = new LazyVar<
    Promise<{
      works: Map<string, AssignmentWork>
      assignments: Assignment[]
      assignmentRosters: AssignmentRoster[]
    }>
  >()
  private rosters = new LazyVar<Promise<Map<string, Roster>>>()
  private studentRosters = new LazyVar<
    Promise<{ rosters: Map<string, Roster> }>
  >()
  private sharedAssignmentWorks = new LazyVar<
    Promise<{ work: Map<string, AssignmentWork> }>
  >()
  private classCodesForAssignment = new Map<string, ClassCode[]>()
  private user = new LazyVar<Promise<User>>()
  private cachedAssignmentId: string | null = null
  private cachedAssignmentWorkId: string | null = null
  private cachedSharedAssignmentWorkId: string | null = null
  private cachedRosterId: string | null = null
  private cachedAssignmentRosterId: string | null = null
  private cachedRosterUsersRosterId: string | null = null
  private cachedSessionDataAssignmentId: string | null = null
  private cachedSessionDataRosterId: string | null = null
  private cachedSharedWorkSessionDataAssignmentId: string | null = null
  private cachedSharedWorkSessionDataRosterId: string | null = null
  private cachedTestSegmentsUserId: string | null = null
  private rosterUsers = new LazyVar<Promise<Map<string, User>>>()
  private assignmentsForRoster = new LazyVar<Promise<Map<string, Assignment>>>()
  private coTeachersForRoster = new LazyVar<Promise<CoTeacher[]>>()
  private sessionData = new LazyVar<Promise<SessionData>>()
  private sharedWorkSessionData = new LazyVar<Promise<SharedWorkSessionData>>()
  private messages = new LazyVar<Promise<MessageSet>>()
  private stickers = new LazyVar<Promise<UserSticker[]>>()
  private adminContracts = new LazyVar<Promise<Contract[]>>()
  private allContracts = new LazyVar<Promise<Contract[]>>()
  private filteredActiveContracts = new LazyVar<Promise<Contract[]>>()
  private filteredNonTrialContracts = new LazyVar<Promise<Contract[]>>()
  private filteredActiveNonTrialContracts = new LazyVar<Promise<Contract[]>>()
  private proInfo = new LazyVar<Promise<ProInfo>>()
  private contractIdToUsers: Record<
    string,
    LazyVar<Promise<Map<string, User>>>
  > = {}
  private testSegments = new LazyVar<Promise<Map<string, string>>>()
  private features = new LazyVar<Promise<Set<string>>>()
  private contractFeatures = new LazyVar<Promise<Map<string, string>>>()
  private childOrganizationCount = new LazyVar<
    Promise<ChildOrganizationCount>
  >()
  private helper: ActiveHelper | null = null
  private userStatus: UserStatusEditor | null = null
  private subscriptions = new LazyVar<Promise<Subscription[]>>()
  private organizations = new LazyVar<Promise<Organization[]>>()
  private unverifiedOrganizations = new LazyVar<Promise<Organization[]>>()
  private helpArticles = new LazyVar<Promise<HelpArticle[]>>()

  constructor(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
    private $q: any,
    private $log: angular.ILogService,
    private $timeout: angular.ITimeoutService,
    private BootstrapService: BootstrapService,
    private AssignmentService: AssignmentService,
    private AssignmentWorkService: AssignmentWorkService,
    private RosterService: RosterService,
    private ClassCodeService: ClassCodeService,
    private UserService: UserService,
    private AuthService: AuthService,
    private MessageService: MessageService,
    private NotificationService: NotificationService,
    private HelpRequestService: HelpRequestService,
    private StickerService: StickerService,
    private ContractService: ContractService,
    private StudentCacheService: StudentCacheService,
    private SubscriptionService: SubscriptionService,
    private ABTestService: ABTestService,
    private StorageService: StorageService,
    private FeedbackService: FeedbackService,
    private FirebaseService: FirebaseService,
    private OrganizationService: OrganizationService,
    private HelpArticleService: HelpArticleService,
    private AnalyticsService: AnalyticsService,
    private SharedWorksService: SharedWorksService,
    private CoTeacherService: CoTeacherService,
  ) {
    "ngInject"

    this.reset()

    this.AuthService.userAuthenticated.subscribe(this.registerUser, this)
    this.AuthService.dataCleared.subscribe(this.reset, this)
  }

  private registerUser(info: UserTokenInfo): void {
    if (info.isTeacher) {
      this.BootstrapService.whenReady
        .then(() => this.getTestSegmentsForUser())
        .then((segments) =>
          this.AnalyticsService.identifyUserAsLoggedIn(info, segments),
        )
    }
  }

  getUser(getFresh = false): Promise<User> {
    if (getFresh) {
      this.user.clear()
    }

    return this.user.value(() => {
      return this.UserService.getUser().catch((err: unknown) => {
        this.user.clear()
        throw err
      })
    })
  }

  updateUser(updatedUser: User): Promise<void> {
    return this.UserService.updateUser(updatedUser).then((tokenResponse) => {
      if (tokenResponse.token) {
        return this.AuthService.processTokenResult(
          tokenResponse.token,
          this.AuthService.rememberMe,
        )
          .then(() => {
            this.reset()
          })
          .then(() => this.user.set(this.$q.resolve(updatedUser)))
      }
    })
  }

  getAssignmentsForUser(getFresh = false): Promise<Map<string, Assignment>> {
    if (getFresh) {
      this.assignments.clear()
    }

    return this.assignments.value(() => {
      return this.AssignmentService.getForCurrentUser()
        .then((array) => {
          return new Map(array.map((assignment) => [assignment.id, assignment]))
        })
        .catch((err) => {
          this.assignments.clear()
          throw err
        })
    })
  }

  getAssignmentForUser(
    assignmentId: string,
    getFresh = false,
  ): Promise<Assignment> {
    return this.getAssignmentsForUser().then((map) => {
      if (getFresh || !map.has(assignmentId)) {
        return this.AssignmentService.get(assignmentId)
          .then((assignment) => {
            if (assignment.ownerId === this.AuthService.currentUserId) {
              map.set(assignmentId, assignment)
              return assignment
            } else {
              throw new Error(`Assignment ${assignmentId} is not available`)
            }
          })
          .catch((err) => {
            map.delete(assignmentId)
            throw err
          })
      } else {
        return map.get(assignmentId)
      }
    })
  }

  getStudentAssignmentsAndWorks(
    studentId: string,
    rosterId: string,
    getFresh = false,
  ): Promise<{
    works: Map<string, AssignmentWork>
    assignments: Assignment[]
    assignmentRosters: AssignmentRoster[]
  }> {
    if (getFresh) {
      this.studentAssignmentsAndWorks.clear()
    }

    return this.studentAssignmentsAndWorks.value(() =>
      this.AssignmentWorkService.getAllForStudent(studentId, rosterId)
        .then(({ works, assignments, assignmentRosters }) => {
          return {
            works: new Map(works.map((w) => [w.id, w])),
            assignments,
            assignmentRosters,
          }
        })
        .catch((err) => {
          this.studentAssignmentsAndWorks.clear()
          throw err
        }),
    )
  }

  addAssignmentForUser(assignment: Assignment): Promise<Assignment> {
    return this.getAssignmentsForUser().then((map) => {
      map.set(assignment.id, assignment)
      return assignment
    })
  }

  updateAssignmentForUser(assignment: Assignment): Promise<void> {
    return this.AssignmentService.update(assignment).then(() => {
      this.getAssignmentsForUser().then((map) => {
        map.set(assignment.id, assignment)
      })
    })
  }

  updateAssignmentModifiedDateForUser(assignment: Assignment): Promise<void> {
    return this.AssignmentService.updateAssignmentModifiedDate(
      assignment.id,
    ).then(() => {
      this.getAssignmentsForUser().then((map) => {
        map.set(assignment.id, assignment)
      })
    })
  }

  deleteAssignment(assignmentId: string): Promise<void> {
    return this.AssignmentService.delete(assignmentId).then(() => {
      return this.getAssignmentsForUser().then((assignments) => {
        assignments.delete(assignmentId)
      })
    })
  }

  deleteFolder(folderId: string): Promise<void> {
    return this.AssignmentService.deleteFolder(folderId).then(() => {
      this.deleteFolderFromCache(folderId)
    })
  }

  deleteFolderFromCache(folderId: string): Promise<void> {
    return this.getAssignmentsForUser().then((assignments) => {
      assignments.delete(folderId)

      assignments.forEach((assignment) => {
        if (assignment.folder === folderId) {
          if (assignment.isFolder) {
            this.deleteFolderFromCache(assignment.id)
          } else {
            assignments.delete(assignment.id)
          }
        }
      })
    })
  }

  getAssignment(assignmentId: string, getFresh = false): Promise<Assignment> {
    if (getFresh || assignmentId !== this.cachedAssignmentId) {
      this.assignment.clear()
    }

    this.cachedAssignmentId = assignmentId

    return this.assignment.value(() => {
      return this.AssignmentService.get(assignmentId).catch((err) => {
        this.assignment.clear()
        this.cachedAssignmentId = null
        throw err
      })
    })
  }

  getAssignmentWork(
    assignment: Assignment,
    assignmentWorkId: string,
    getFresh = false,
  ): Promise<AssignmentWork> {
    const newAssignmentWorkId = `${assignment.id}|${assignmentWorkId}`

    if (getFresh || newAssignmentWorkId !== this.cachedAssignmentWorkId) {
      this.assignmentWork.clear()
    }

    this.cachedAssignmentWorkId = newAssignmentWorkId

    return this.assignmentWork.value(() => {
      return this.AssignmentWorkService.getForAssignment(
        assignment,
        assignmentWorkId,
      ).catch((err) => {
        this.assignmentWork.clear()
        this.cachedAssignmentWorkId = null
        throw err
      })
    })
  }

  getRosterWorks(
    assignment: Assignment,
    rosterId: string,
    getFresh = false,
  ): Promise<Map<string, AssignmentWork>> {
    const newAssignmentRosterId = `${assignment.id}|${rosterId}`

    if (getFresh || newAssignmentRosterId !== this.cachedAssignmentRosterId) {
      this.assignmentWorks.clear()
    }

    this.cachedAssignmentRosterId = newAssignmentRosterId

    return this.assignmentWorks.value(() => {
      return this.AssignmentWorkService.getAllForAssignmentRoster(
        assignment,
        rosterId,
      )
        .then((result) => {
          const map = new Map<string, AssignmentWork>()
          const owners = new Set<string>()

          // In case of duplicates for an owner, always prefer the first one found
          result.forEach((work) => {
            if (!owners.has(work.ownerId)) {
              map.set(work.id, work)
              owners.add(work.ownerId)
            }
          })

          return map
        })
        .catch((err) => {
          this.assignmentWorks.clear()
          this.cachedAssignmentRosterId = null
          throw err
        })
    })
  }

  getRostersForUser(getFresh = false): Promise<Map<string, Roster>> {
    if (getFresh) {
      this.rosters.clear()
    }

    return this.rosters.value(() => {
      return this.RosterService.getUserRosters()
        .then((array) => {
          return new Map(array.map((roster) => [roster.id, roster]))
        })
        .catch((err) => {
          this.rosters.clear()
          throw err
        })
    })
  }

  getStudentRosters(
    studentId: string,
    getFresh = false,
  ): Promise<{ rosters: Map<string, Roster> }> {
    if (getFresh) {
      this.studentRosters.clear()
    }

    return this.studentRosters.value(() =>
      this.RosterService.getStudentRosters(studentId)
        .then((rosters) => {
          return new Map(rosters.map((roster) => [roster.id, roster]))
        })
        .catch((err) => {
          this.studentRosters.clear()
          throw err
        }),
    )
  }

  addRosterForUser(roster: Roster): Promise<Roster> {
    return this.getRostersForUser().then((map) => {
      map.set(roster.id, roster)
      return roster
    })
  }

  updateRosterForUser(roster: Roster): void {
    this.getRostersForUser().then((rosters) => {
      if (rosters.has(roster.id)) {
        rosters.set(roster.id, roster)
      }
    })
  }

  deleteRoster(rosterId: string): Promise<void> {
    return this.RosterService.delete(rosterId).then(() => {
      return this.getRostersForUser().then((rosters) => {
        rosters.delete(rosterId)
      })
    })
  }

  getClassCodesForUserAssignment(
    assignmentId: string,
    getFresh = false,
  ): Promise<ClassCode[]> {
    if (getFresh) {
      this.classCodesForAssignment.delete(assignmentId)
    } else {
      const cachedClassCodes = this.classCodesForAssignment.get(assignmentId)
      if (cachedClassCodes) return Promise.resolve(cachedClassCodes)
    }

    return this.ClassCodeService.getForCurrentUserAssignment(assignmentId)
      .then((classCodes) => {
        this.classCodesForAssignment.set(assignmentId, classCodes)
        return classCodes
      })
      .catch((err) => {
        this.classCodesForAssignment.delete(assignmentId)
        throw err
      })
  }

  getRosterUsers(
    rosterId: string,
    getFresh = false,
  ): Promise<Map<string, User>> {
    if (getFresh || this.cachedRosterUsersRosterId !== rosterId) {
      this.rosterUsers.clear()
    }

    this.cachedRosterUsersRosterId = rosterId
    return this.rosterUsers.value(() => {
      return this.RosterService.getUsersForRoster(rosterId)
        .then((result) => {
          return new Map(result.map((user) => [user.id, user]))
        })
        .catch((err) => {
          this.rosterUsers.clear()
          this.cachedRosterUsersRosterId = null
          throw err
        })
    })
  }

  getRosterUser(
    rosterId: string,
    userId: string,
    getFresh = false,
  ): Promise<User> {
    return this.getRosterUsers(rosterId).then((map) => {
      if (getFresh) {
        return this.RosterService.getUserForRoster(rosterId, userId)
          .then((user) => {
            map.set(user.id, user)
            return user
          })
          .catch((err) => {
            map.delete(userId)
            throw err
          })
      } else {
        return map.get(userId)
      }
    })
  }

  updateUserForRosterUsers(rosterId: string, user: User): void {
    if (this.cachedRosterUsersRosterId === rosterId) {
      this.getRosterUsers(rosterId).then((result) => {
        if (result.has(user.id)) {
          result.set(user.id, user)
        }
      })
    }
  }

  removeUserForRosterUsers(rosterId: string, user: User): void {
    if (this.cachedRosterUsersRosterId === rosterId) {
      this.getRosterUsers(rosterId).then((result) => {
        result.delete(user.id)
      })
    }
  }

  getMessagesForUser(): Promise<MessageSet> {
    return this.messages.value(() => {
      return this.MessageService.getReceivedMessagesForCurrentUser()
        .then((result) => {
          const value = new MessageSet(
            result,
            this.NotificationService.getNewMessageNotificationForCurrentUser(),
            this.MessageService,
          )

          value.start()
          return value
        })
        .catch((err) => {
          this.clearMessages()
          throw err
        })
    })
  }

  private clearMessages(): void {
    if (this.messages) {
      this.messages.clear((value) =>
        value.then((messageSet) => messageSet.destroy()),
      )
    }
  }

  getSessionData(
    assignmentId: string,
    rosterId: string,
    getFresh = false,
    skipRefresh = false,
  ): Promise<SessionData> {
    if (
      getFresh ||
      this.cachedSessionDataAssignmentId !== assignmentId ||
      this.cachedSessionDataRosterId !== rosterId
    ) {
      this.clearSessionData()
    }

    // If we already have an instance of the correct session data, simply refresh the data
    if (this.sessionData.isSet) {
      return this.sessionData.value().then((sessionData) => {
        return skipRefresh ? sessionData : sessionData.refreshData()
      })
    }
    // Otherwise build one anew
    else {
      this.cachedSessionDataAssignmentId = assignmentId
      this.cachedSessionDataRosterId = rosterId

      return this.sessionData.value(() => {
        return SessionData.fetch(
          assignmentId,
          rosterId,
          this.$q,
          this.$timeout,
          this,
          this.HelpRequestService,
          this.AssignmentWorkService,
          this.UserService,
          this.NotificationService,
          this.AssignmentService,
          this.StorageService,
          this.FeedbackService,
          this.FirebaseService,
        )
          .then((sessionData) => {
            if (!sessionData.isComplete) {
              this.clearSessionData()
            }
            return sessionData
          })
          .catch((err) => {
            this.clearSessionData()
            throw err
          })
      })
    }
  }

  //-------------- Student helpers and student status

  setUserStatus(
    assignmentId: string,
    questionId: string,
    activity: string,
    helpeeId?: string,
  ): void {
    if (
      this.userStatus &&
      this.userStatus.userId !== this.AuthService.currentUserId
    ) {
      this.clearUserStatus()
    }

    if (!this.userStatus) {
      const currentUserId = this.AuthService.currentUserId
      if (!currentUserId) throw new Error("No current user")

      this.userStatus =
        this.NotificationService.getUserStatusEditor(currentUserId).start()
    }

    this.userStatus.setStatus(
      true,
      assignmentId,
      questionId,
      activity,
      helpeeId,
    )
  }

  /**
   * Sets the user status to offline
   */
  clearUserStatus(): void {
    if (this.userStatus) {
      this.userStatus.stop()
      this.userStatus = null
    }
  }

  clearUserStatusDetails(): void {
    if (this.userStatus) {
      this.userStatus.assignmentId = null
      this.userStatus.questionId = null
      this.userStatus.activity = null
    }
  }

  startHelping(
    assignment: Assignment,
    helperId: string,
    helperName: string,
    helperRole: string,
    helpeeId: string,
    assignmentId: string,
    questionId: string,
  ): void {
    this.stopHelping()
    const helper = new Helper(
      helperId,
      helperName,
      helperRole,
      helpeeId,
      assignmentId,
      questionId,
    )
    this.helper = this.NotificationService.getActiveHelper(
      assignment,
      helper,
    ).start()
    this.helper.setHelper()
  }

  stopHelping(): void {
    if (this.helper) {
      this.helper.stop()
      this.helper = null
    }
  }

  getStickersForUser(getFresh = false): Promise<UserSticker[]> {
    const currentUserId = this.AuthService.currentUserId
    if (!currentUserId) throw new Error("No current user")

    if (getFresh) {
      this.stickers.clear()
    }

    return this.stickers.value(() => {
      return this.StickerService.getForUser(currentUserId).catch((err) => {
        this.stickers.clear()
        throw err
      })
    })
  }

  /**
   * Saves a new sticker and ensures that the cached collection reflects this new version
   *
   * @param imageUrl {string}
   * @param text {string}
   * @param tags {Array}
   * @returns {Promise.<UserSticker>}
   */
  createSticker(
    imageUrl: string,
    text: string,
    tags: string[],
  ): Promise<UserSticker> {
    return this.StickerService.create(imageUrl, text, tags).then((sticker) => {
      this.getStickersForUser(false).then((array) => {
        array.push(sticker)
      })

      return sticker
    })
  }

  updateSticker(
    sticker: UserSticker,
    beforeStickerId: string,
  ): Promise<UserSticker> {
    return this.StickerService.update(sticker, beforeStickerId)
  }

  deleteSticker(stickerId: string): Promise<void> {
    return this.StickerService.delete(stickerId).then(() => {
      return this.getStickersForUser(false).then((stickers) => {
        const index = stickers.map((sticker) => sticker.id).indexOf(stickerId)
        index > -1 && stickers.splice(index, 1)
      })
    })
  }

  getAdminContracts(): Promise<Contract[]> {
    return this.adminContracts.value(() => {
      const contracts = this.AuthService.authData.contracts
      return this.$q
        .all(
          Object.keys(contracts)
            .filter((key) => contracts[key].admin === true)
            .map((key) => this.ContractService.get(key)),
        )
        .catch((err: unknown) => {
          this.adminContracts.clear()
          throw err
        })
    })
  }

  getContracts(
    getFresh = false,
    includeExpired = false,
    excludeTrials = false,
  ): Promise<Contract[]> {
    const currentUserId = this.AuthService.currentUserId
    if (!currentUserId) throw new Error("No current user")

    if (getFresh) {
      this.allContracts.clear()
      this.filteredActiveContracts.clear()
      this.filteredNonTrialContracts.clear()
      this.filteredActiveNonTrialContracts.clear()
    }

    let property = this.allContracts
    if (includeExpired) {
      if (excludeTrials) {
        property = this.filteredNonTrialContracts
      } else {
        property = this.allContracts
      }
    } else {
      if (excludeTrials) {
        property = this.filteredActiveNonTrialContracts
      } else {
        property = this.filteredActiveContracts
      }
    }

    return property.value(() => {
      return this.ContractService.getContractsForUser(
        currentUserId,
        includeExpired,
        excludeTrials,
      ).catch((err) => {
        property.clear()
        throw err
      })
    })
  }

  getAppConfig(getFresh = false): Promise<AppConfiguration> {
    return this.getUserFeatures(getFresh)
      .then((features) => {
        return new AppConfiguration(features)
      })
      .catch((err) => {
        this.$log.error("Unable to get configuration")
        throw err
      })
  }

  getProInfo(getFresh = false): Promise<ProInfo> {
    if (getFresh) {
      this.proInfo.clear()
    }

    return this.proInfo.value(() => {
      return this.$q
        .all({
          features: this.getFeatures(getFresh),
          contracts: this.getContracts(getFresh),
        })
        .then(
          ({
            features,
            contracts,
          }: {
            features: Set<string>
            contracts: Contract[]
          }) => {
            return new ProInfo(features, contracts)
          },
        )
        .catch((err: unknown) => {
          this.proInfo.clear()
          throw err
        })
    })
  }

  getUsersForContract(
    contractId: string,
    getFresh = false,
  ): Promise<Map<string, User>> {
    if (!this.contractIdToUsers[contractId]) {
      this.contractIdToUsers[contractId] = new LazyVar()
    }
    const container = this.contractIdToUsers[contractId]

    if (getFresh) {
      container.clear()
    }

    return container.value(() => {
      return this.ContractService.getUsers(contractId)
        .then((array) => new Map(array.map((x) => [x.id, x])))
        .catch((err) => {
          container.clear()
          throw err
        })
    })
  }

  /**
   * Gets and caches the inactive users for a contract
   *
   * Returns a Map of contract ids to Map of inactive users within that contract. The
   * User map is keyed by user id.
   */
  getInactiveUsersForContract(
    contractId: string,
    monthsInactive: number,
    getFresh = false,
  ): Promise<Map<string, User>> {
    if (!this.contractIdToUsers[contractId]) {
      this.contractIdToUsers[contractId] = new LazyVar()
    }
    const inactiveUsersInContract = this.contractIdToUsers[contractId]

    if (getFresh) {
      inactiveUsersInContract.clear()
    }

    return inactiveUsersInContract.value(() => {
      return this.ContractService.getInactiveUsers(contractId, monthsInactive)
        .then((array) => new Map(array.map((x) => [x.id, x])))
        .catch((err) => {
          inactiveUsersInContract.clear()
          throw err
        })
    })
  }

  /**
   * Finds all users for all contracts of the currently logged in user.
   *
   * Returns a Map of contract ids to Map of users within that contract. The
   * User map is keyed by user id.
   */
  getUsersForContracts(
    getFresh = false,
  ): Promise<Map<string, Map<string, User>>> {
    const contractIds = this.AuthService.authData.contractIds
    const promises = contractIds.map((id: string) =>
      this.getUsersForContract(id, getFresh),
    )
    return this.$q
      .all(promises)
      .then((contractUserArray: Array<Map<string, User>>) => {
        return new Map(
          contractIds.map((contractId, index) => [
            contractId,
            contractUserArray[index],
          ]),
        )
      })
  }

  /**
   * Finds all inactive users over monthsInactive for all contracts of the currently logged in user.
   *
   * Returns a Map of contract ids to Map of users within that contract. The
   * User map is keyed by user id.
   */
  getInactiveUsersForContracts(
    monthsInactive: number,
    getFresh = false,
  ): Promise<Map<string, Map<string, User>>> {
    const contractIds = this.AuthService.authData.contractIds
    const promises = contractIds.map((id: string) =>
      this.getInactiveUsersForContract(id, monthsInactive, getFresh),
    )
    return this.$q
      .all(promises)
      .then((contractUserArray: Array<Map<string, User>>) => {
        return new Map(
          contractIds.map((contractId, index) => [
            contractId,
            contractUserArray[index],
          ]),
        )
      })
  }

  getFeatures(getFresh = false): Promise<Set<string>> {
    if (getFresh) {
      this.features.clear()
    }

    return this.features.value(() => {
      return this.getUserFeatures(getFresh)
        .then((data) => new Set(data.features))
        .catch((err) => {
          this.features.clear()
          throw err
        })
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getUserFeatures(getFresh = false): Promise<any> {
    if (getFresh) {
      this.contractFeatures.clear()
    }

    return this.contractFeatures.value(() => {
      return this.ContractService.getFeatures()
        .then((data) => {
          return data
        })
        .catch((err) => {
          this.contractFeatures.clear()
          throw err
        })
    })
  }

  /**
   * Returns a map of test segment ids to test variables
   */
  getTestSegmentsForUser(): Promise<Map<string, string>> {
    const currentUserId = this.AuthService.currentUserId
    if (!currentUserId) throw new Error("No current user")

    if (this.cachedTestSegmentsUserId !== this.AuthService.currentUserId) {
      this.testSegments.clear()
    }

    this.cachedTestSegmentsUserId = currentUserId

    return this.testSegments.value(() => {
      return this.ABTestService.getTestSegmentsForUser(currentUserId).catch(
        (err) => {
          this.testSegments.clear()
          this.cachedTestSegmentsUserId = null
          throw err
        },
      )
    })
  }

  /**
   * Indicates whether a user is part of a test segment. If they are 'A', returns true.
   * If they are any other segment, returns false.
   */
  getTestSegment(testSegmentId: string): Promise<boolean> {
    return this.getTestSegmentsForUser()
      .then((testSegments) => {
        return testSegments.get(testSegmentId) === ABTestVariable.A
      })
      .catch((err) => {
        this.testSegments.clear()
        throw err
      })
  }

  clearSessionData(): void {
    if (this.sessionData) {
      this.cachedSessionDataAssignmentId = null
      this.cachedSessionDataRosterId = null
      this.sessionData.clear((value) =>
        value.then((sessionData) => sessionData.destroy()),
      )
    }
  }

  clearSharedWorkSessionData(): void {
    if (this.sharedWorkSessionData) {
      this.cachedSharedWorkSessionDataAssignmentId = null
      this.cachedSharedWorkSessionDataRosterId = null
      this.sharedWorkSessionData.clear()
    }
  }

  getSubscriptions(getFresh = false): Promise<Subscription[]> {
    const currentUserId = this.AuthService.currentUserId
    if (!currentUserId) throw new Error("No current user")

    if (getFresh) {
      this.subscriptions.clear()
    }

    return this.subscriptions.value(() => {
      return this.SubscriptionService.getSubscriptions(currentUserId).catch(
        (err) => {
          this.subscriptions.clear()
          throw err
        },
      )
    })
  }

  getOrganizations(
    getFresh = false,
    getDistricts = false,
  ): Promise<Organization[]> {
    const currentUserId = this.AuthService.currentUserId
    if (!currentUserId) throw new Error("No current user")

    if (getFresh) {
      this.organizations.clear()
    }

    return this.organizations.value(() => {
      return this.$q
        .all({
          organizations: this.OrganizationService.getForUser(currentUserId),
          contracts: this.getContracts(getFresh),
          districts:
            getDistricts &&
            this.OrganizationService.getDistrictsForUser(currentUserId),
        })
        .then(
          ({
            organizations,
            contracts,
            districts,
          }: {
            organizations: Organization[]
            contracts: Contract[]
            districts: Organization[]
          }) => {
            const organizationMap = new Map<string, Organization>()

            const allOrganizations = organizations.map((organization) => {
              organization.contract = contracts.find(
                (contract) =>
                  organization.contractId &&
                  contract.id === organization.contractId,
              )
              organization.admin =
                this.AuthService.authData &&
                this.AuthService.authData.isContractAdmin(
                  organization.contractId,
                )
              organizationMap.set(organization.id, organization)
              return organization
            })

            if (districts && districts.length) {
              districts.forEach((district) => {
                if (!organizationMap.has(district.id)) {
                  allOrganizations.push(district)
                }
              })
            }
            return allOrganizations
          },
        )
        .catch((err: unknown) => {
          this.organizations.clear()
          throw err
        })
    })
  }

  getUnverifiedOrganizations(getFresh = false): Promise<Organization[]> {
    const currentUserId = this.AuthService.currentUserId
    if (!currentUserId) throw new Error("No current user")

    if (getFresh) {
      this.unverifiedOrganizations.clear()
    }

    return this.unverifiedOrganizations.value(() => {
      return this.OrganizationService.getUnverifiedOrganizations(
        currentUserId,
      ).catch((err) => {
        this.unverifiedOrganizations.clear()
        throw err
      })
    })
  }

  getSchools(getFresh = false): Promise<Organization[]> {
    return this.getOrganizations(getFresh).then((organizations) => {
      return organizations.filter(
        (org) => org.type === OrganizationTypes.School,
      )
    })
  }

  getArticles(getFresh = false): Promise<HelpArticle[]> {
    if (getFresh) {
      this.helpArticles.clear()
    }

    return this.helpArticles.value(() => {
      return this.HelpArticleService.getAll().catch((err: unknown) => {
        this.helpArticles.clear()
        throw err
      })
    })
  }

  getArticle(
    articleId: string,
    getFresh = false,
  ): Promise<HelpArticle | undefined> {
    return this.getArticles(getFresh).then((articles) => {
      const article = articles.find((a) => a.id === articleId)
      if (article) {
        return article
      } else {
        return this.getArticles(true).then((articlesAgain) =>
          articlesAgain.find((a) => a.id === articleId),
        )
      }
    })
  }

  createArticle(
    categoryId: string,
    sectionId: string,
    title?: string,
    body?: string,
    priority?: number,
  ): Promise<HelpArticle> {
    return this.HelpArticleService.create(
      categoryId,
      sectionId,
      title,
      body,
      priority,
    ).then((result) => {
      this.helpArticles.clear()
      return result
    })
  }

  /**
   * Clears all data contained in the service
   */
  reset(): void {
    this.assignment = new LazyVar()
    this.assignmentWork = new LazyVar()
    this.assignmentWorks = new LazyVar()
    this.assignments = new LazyVar()
    this.studentAssignmentsAndWorks = new LazyVar()
    this.rosters = new LazyVar()
    this.studentRosters = new LazyVar()
    this.sharedAssignmentWorks = new LazyVar()
    this.classCodesForAssignment = new Map()
    this.user = new LazyVar()
    this.cachedAssignmentId = null
    this.cachedAssignmentWorkId = null
    this.cachedSharedAssignmentWorkId = null
    this.cachedRosterId = null
    this.cachedAssignmentRosterId = null
    this.cachedRosterUsersRosterId = null
    this.cachedSessionDataAssignmentId = null
    this.cachedSessionDataRosterId = null
    this.cachedSharedWorkSessionDataAssignmentId = null
    this.cachedSharedWorkSessionDataRosterId = null
    this.cachedTestSegmentsUserId = null
    this.rosterUsers = new LazyVar()
    this.assignmentsForRoster = new LazyVar()
    this.coTeachersForRoster = new LazyVar()

    this.clearSessionData()
    this.sessionData = new LazyVar()

    this.sharedWorkSessionData = new LazyVar()

    this.clearMessages()
    this.messages = new LazyVar()

    this.stickers = new LazyVar()

    this.adminContracts = new LazyVar()
    this.allContracts = new LazyVar()
    this.filteredActiveContracts = new LazyVar()
    this.filteredNonTrialContracts = new LazyVar()
    this.filteredActiveNonTrialContracts = new LazyVar()

    this.proInfo = new LazyVar()

    this.contractIdToUsers = {}

    this.testSegments = new LazyVar()
    this.features = new LazyVar()
    this.contractFeatures = new LazyVar()
    this.childOrganizationCount = new LazyVar()

    this.stopHelping()
    this.helper = null

    this.clearUserStatus()
    this.userStatus = null
    this.subscriptions = new LazyVar()
    this.organizations = new LazyVar()
    this.unverifiedOrganizations = new LazyVar()
    this.helpArticles = new LazyVar()

    this.StudentCacheService.reset()
  }

  getChildOrganizationCount(
    organizationId: string,
    getFresh = false,
  ): Promise<ChildOrganizationCount> {
    if (getFresh) {
      this.childOrganizationCount.clear()
    }

    return this.childOrganizationCount.value(() => {
      return this.OrganizationService.getChildOrganizationCount(
        organizationId,
      ).catch((err) => {
        this.childOrganizationCount.clear()
        throw err
      })
    })
  }

  getAssignmentsByRoster(
    rosterId: string,
    getFresh = false,
  ): Promise<Map<string, Assignment>> {
    if (getFresh || this.cachedRosterId !== rosterId) {
      this.assignmentsForRoster.clear()
      this.cachedRosterId = rosterId
    }

    return this.assignmentsForRoster.value(() => {
      return this.AssignmentService.getRosterAssignments(rosterId)
        .then((assignments) => {
          return new Map(
            assignments.map((assignment) => [assignment.id, assignment]),
          )
        })
        .catch((err) => {
          this.assignmentsForRoster.clear()
          throw err
        })
    })
  }

  removeAssignmentFromRoster(
    assignmentId: string,
    rosterId: string,
  ): Promise<void> {
    if (this.cachedRosterId !== rosterId) {
      this.assignmentsForRoster.clear()
      this.cachedRosterId = rosterId
    }
    return this.AssignmentService.removeRoster(assignmentId, rosterId)
      .then(() => {
        return this.getAssignmentsByRoster(rosterId).then(
          (assignmentsByRoster) => {
            assignmentsByRoster.delete(assignmentId)
          },
        )
      })
      .catch((err) => {
        this.assignmentsForRoster.clear()
        throw err
      })
  }

  getActiveAssignmentsForBasicUsers(
    getFresh = false,
  ): Promise<Map<string, Assignment> | null> | null {
    if (this.AuthService.authData.isFree) {
      return this.getTestSegment(ABTest.AssignmentLimit20).then((segment) => {
        if (segment === true) {
          return this.getAssignmentsForUser(getFresh).then((assignmentsMap) => {
            const topAssignments = lodash
              .orderBy(
                Array.from(assignmentsMap.values()),
                (assignment) => {
                  return assignment.lastModified
                },
                ["desc"],
              )
              .slice(0, 20)
            return new Map(
              topAssignments.map((assignment) => [assignment.id, assignment]),
            )
          })
        }
        return null
      })
    }
    return null
  }

  getSharedAssignmentWork(
    sharedWorkId: string,
    getFresh = false,
  ): Promise<{ work: Map<string, AssignmentWork> }> {
    if (getFresh || sharedWorkId !== this.cachedSharedAssignmentWorkId) {
      this.sharedAssignmentWorks.clear()
    }
    this.cachedSharedAssignmentWorkId = sharedWorkId

    return this.sharedAssignmentWorks.value(() => {
      return this.SharedWorksService.getSharedAssignmentWork(
        sharedWorkId,
      ).catch((err) => {
        this.sharedAssignmentWorks.clear()
        this.cachedAssignmentWorkId = null
        throw err
      })
    })
  }

  getSharedWorkSessionData(
    assignmentOwner: SharedWorkUser,
    student: SharedWorkUser,
    assignmentWork: AssignmentWork,
    rosterId: string,
    getFresh = false,
  ): Promise<SharedWorkSessionData> {
    if (
      getFresh ||
      this.cachedSharedWorkSessionDataAssignmentId !==
        assignmentWork.assignment.id ||
      this.cachedSharedWorkSessionDataRosterId !== rosterId
    ) {
      this.clearSharedWorkSessionData()
    }

    this.cachedSharedWorkSessionDataAssignmentId = assignmentWork.assignment.id
    this.cachedSharedWorkSessionDataRosterId = rosterId

    return this.sharedWorkSessionData.value(() => {
      return SharedWorkSessionData.fetch(
        assignmentOwner,
        student,
        assignmentWork,
        this.$q,
      ).catch((err) => {
        this.clearSharedWorkSessionData()
        throw err
      })
    })
  }

  getCoTeachersByRoster(
    rosterId: string,
    getFresh = false,
  ): Promise<CoTeacher[]> {
    if (getFresh || this.cachedRosterId !== rosterId) {
      this.coTeachersForRoster.clear()
      this.cachedRosterId = rosterId
    }

    return this.coTeachersForRoster.value(() => {
      return this.CoTeacherService.getInvitedCoTeachersForRosters(rosterId)
        .then((coTeachers) => coTeachers)
        .catch((err) => {
          this.coTeachersForRoster.clear()
          throw err
        })
    })
  }
}
